// src/lib/pushNotifications.ts
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { isNativePlatform } from '../utils/platformUtil';
import { store } from '../redux-stores/store';
import { Preferences } from '@capacitor/preferences';
import { setEnableNotificationsPopupVisibility } from '../redux-stores/slices/layoutSlice';

let isInitialized = false;

/**
 * Initializes push notification listeners using Firebase Messaging plugin.
 * Optimized for iOS cold starts by ensuring listeners are bound before 
 * checking for the notification that launched the app.
 */

const SHOW_PROPMPT_DELAY = 10 * 60 * 1000; // 10 minutes
const LAST_PROMPT_TIMESTAMP_KEY = 'last_notification_prompt_timestamp';

export function initPushListeners(onAction: (data: any) => void, onReceive?: (data: any) => void) {
    if (!isNativePlatform()) return;
    
    // If we're already initialized, we don't want to double-bind,
    // but we do want to ensure listeners are active.
    if (isInitialized) return;
    isInitialized = true;

    // 1. Attach listeners immediately.
    // These handle notifications while the app is already running (background/foreground).
    FirebaseMessaging.addListener("notificationReceived", (event) => {
        console.log("Firebase Push received (foreground):", JSON.stringify(event));
        onReceive?.(event.notification);
    });

    FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
        console.log("Firebase Push action performed:", JSON.stringify(event));
        const data = event.notification.data;
        if (data) {
            onAction(data);
        }
    });

    // 2. THE FIX FOR iOS COLD STARTS & CASCADING FAILURES:
    // We manually check for delivered notifications to see if one triggered the app open.
    const checkInitialNotification = async () => {
        try {
            // Wait a brief moment to ensure the React environment and Router are stable.
            // If the first navigation fails, subsequent ones often fail because the 
            // native bridge gets out of sync with the JS state.
            await new Promise(resolve => setTimeout(resolve, 500));

            const result = await FirebaseMessaging.getDeliveredNotifications();
            
            if (result.notifications && result.notifications.length > 0) {
                console.log('Found delivered notifications on startup:', result.notifications.length);
                
                // We prioritize the most recent notification that has a route.
                const lastNotification = result.notifications[0];
                
                // Fix: Cast data to 'any' or check property existence to satisfy TypeScript
                const data = lastNotification.data as any;
                
                if (data && data.route) {
                    console.log('Manually triggering action for initial notification');
                    onAction(data);
                    
                    // The selected code below clears the tray. We keep this to prevent 
                    // the app from re-processing the same notification on every resume.
                    await FirebaseMessaging.removeAllDeliveredNotifications();
                }
            }
            
        } catch (error) {
            console.error('Firebase setup check failed:', error);
            // If setup fails, allow for a retry by resetting the initialization flag
            isInitialized = false;
        }
    };

    checkInitialNotification();
}

export type NotifPermission = 'granted' | 'denied' | 'prompt';

export const getNotificationPermissionStatus = async (): Promise<NotifPermission | null> => {
  if (!isNativePlatform()) return null;

  const perm = await FirebaseMessaging.checkPermissions();

  return perm.receive as NotifPermission;
};

export const showNotificationEnablePrompt = async () => {
  if (!isNativePlatform()) return false;

  const perm = await getNotificationPermissionStatus();

  if (perm !== 'granted'){
    const lastPromptedAt = await Preferences.get({ key: LAST_PROMPT_TIMESTAMP_KEY });
    const now = Date.now();

    if (lastPromptedAt.value) {
      const lastPromptTime = Number(lastPromptedAt.value);
      if ((now - lastPromptTime) < SHOW_PROPMPT_DELAY) {
        // Prompted recently, skip showing again
        console.log('Notification prompt shown recently, skipping...');
        return false;
      }
    }

    // Update the last prompted timestamp
    await Preferences.set({ key: LAST_PROMPT_TIMESTAMP_KEY, value: String(now) });
    setTimeout(() => {
        store.dispatch(setEnableNotificationsPopupVisibility(true));
    }, 3000)
    return true;
  }
}